import { useMemo, useEffect, useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Box } from "@mui/material";
import FormField from "./FormField";
import TextInput from "./TextInput";
import MuiSelectField from "./MuiSelectField";
import useRequiredRule from "../../utils/useRequiredRule";
import {
  formatLocalPhone,
  getLocalPlaceholder,
  getLocalMaxLength,
} from "../../../frontend/utils/phoneMasks";

const profileFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "white",
  },
  "& .MuiInputBase-input": {
    boxShadow: "none !important",
    ":required": {
      boxShadow: "none !important",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#DCDCE4",
  },
};

export default function PhoneFieldWithSelect({
  countryCodeName,
  phoneName,
  codeOptions = [],
  label,
  hint,
  size,
  registerOptions,
  disabled,
  required,
  labelSx,
  defaultOptionId,
}) {
  const { control, setValue } = useFormContext();

  const selectedId = useWatch({ control, name: countryCodeName });

  const prevDisabledRef = useRef(!!disabled);
  const [selectKey, setSelectKey] = useState(0);

  useEffect(() => {
    const isNowDisabled = !!disabled;
    if (isNowDisabled && !prevDisabledRef.current) {
      if (defaultOptionId != null) {
        setValue(countryCodeName, defaultOptionId);
      }
      setSelectKey((k) => k + 1);
    }
    prevDisabledRef.current = isNowDisabled;
  }, [disabled, defaultOptionId, countryCodeName, setValue]);

  const selectedOption = useMemo(
    () => codeOptions.find((o) => o.value === selectedId),
    [codeOptions, selectedId]
  );

  const flag = selectedOption?.flag || "BR";
  const prevFlagRef = useRef(flag);

  // Clear phone when user changes DDI — skip when disabled (programmatic reset)
  useEffect(() => {
    if (prevFlagRef.current !== flag) {
      if (!disabled) setValue(phoneName, "");
      prevFlagRef.current = flag;
    }
  }, [flag, phoneName, setValue, disabled]);

  const formattedOptions = useMemo(
    () =>
      codeOptions.map(({ value, code, text, flag: f }) => ({
        value,
        label: text ? `${text.split(" +")[0]}: +${code}` : `+${code}`,
        flag: f,
      })),
    [codeOptions]
  );

  const phoneRules = useRequiredRule(required, registerOptions);

  const placeholder = getLocalPlaceholder(flag);
  const maxLength = getLocalMaxLength(flag);

  return (
    <FormField
      name={phoneName}
      label={label}
      hint={hint}
      error={null}
      disabled={disabled}
      required={required}
      labelSx={labelSx}
    >
      <Box display="flex" alignItems="flex-start" width="100%">
        <Box flexShrink={0} flex={1} minWidth="160px" mr={1}>
          <MuiSelectField
            key={selectKey}
            name={countryCodeName}
            options={formattedOptions}
            placeholder="+00"
            disabled={disabled}
            required={required}
            isClearable={false}
            sx={{
              ...profileFieldSx,
              ".MuiInputBase-root": {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                maxHeight: "40px",
                borderColor: "#DCDCE4",
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Box flex={2}>
          <Controller
            name={phoneName}
            control={control}
            rules={phoneRules}
            render={({
              field: { ref, value: rawValue = "", onChange, onBlur },
              fieldState: { error },
            }) => {
              const localDigits = String(rawValue).replace(/\D/g, "");
              const displayValue = formatLocalPhone(flag, localDigits);

              return (
                <TextInput
                  inputRef={ref}
                  size={size}
                  sx={profileFieldSx}
                  placeholder={placeholder}
                  required={required}
                  fullWidth
                  value={displayValue}
                  onChange={(e) => {
                    const nextDigits = e.target.value.replace(/\D/g, "");
                    if (nextDigits.length <= maxLength) {
                      onChange(nextDigits);
                    }
                  }}
                  onBlur={onBlur}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Box>
      </Box>
    </FormField>
  );
}
